import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

const EXPENSE_CATEGORIES = ["Catering", "Lodging", "Transportation", "Activities", "Supplies", "Other"];

const CATEGORY_COLORS = {
  Catering: "#2E6B3E",
  Lodging: "#C8972B",
  Transportation: "#3B82F6",
  Activities: "#8B5CF6",
  Supplies: "#E67E22",
  Other: "#6B7280",
};

// Pure-SVG donut chart — no library. Starts at 12 o'clock, draws clockwise.
const SvgPieChart = ({ data, size = 160 }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!total) return <p style={{ color: "var(--muted)", fontSize: "0.8rem" }}>No data</p>;
  const cx = size / 2, cy = size / 2, r = size / 2 - 6, ir = r * 0.52;
  let angle = -Math.PI / 2;
  const slices = data.map(d => {
    const sweep = (d.value / total) * 2 * Math.PI;
    const end = angle + sweep;
    const cos0 = Math.cos(angle), sin0 = Math.sin(angle);
    const cos1 = Math.cos(end),   sin1 = Math.sin(end);
    const large = sweep > Math.PI ? 1 : 0;
    const path = [
      `M${cx + ir * cos0},${cy + ir * sin0}`,
      `L${cx + r  * cos0},${cy + r  * sin0}`,
      `A${r},${r},0,${large},1,${cx + r  * cos1},${cy + r  * sin1}`,
      `L${cx + ir * cos1},${cy + ir * sin1}`,
      `A${ir},${ir},0,${large},0,${cx + ir * cos0},${cy + ir * sin0}`,
      "Z",
    ].join(" ");
    angle = end;
    return { ...d, path, pct: (d.value / total * 100).toFixed(1) };
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth={2} />
        ))}
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 130 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.78rem" }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ color: "var(--charcoal)", flex: 1 }}>{s.label}</span>
            <span style={{ fontWeight: 700 }}>{s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Receipt preview helper with token authentication support
const ReceiptPreview = ({ expenseId, filename }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (filename.toLowerCase().endsWith(".pdf")) {
      setLoading(false);
      return;
    }
    
    let active = true;
    api.get(`/api/finance/expenses/${expenseId}/receipt`, { responseType: "blob" })
      .then(res => {
        if (active) {
          const url = URL.createObjectURL(res.data);
          setSrc(url);
        }
      })
      .catch(err => console.error(err))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      if (src) URL.revokeObjectURL(src);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseId, filename]);

  if (loading) return <div style={{ fontSize: "0.85rem", color: "var(--muted)", padding: 20 }}>Loading preview…</div>;
  
  if (filename.toLowerCase().endsWith(".pdf")) {
    return (
      <div style={{ padding: 20, textAlign: "center" }}>
        <span style={{ fontSize: "2rem" }}>📄</span>
        <div style={{ fontWeight: 600, fontSize: "0.85rem", marginTop: 8 }}>PDF Document</div>
        <span className="text-muted" style={{ fontSize: "0.7rem", wordBreak: "break-all", display: "block", marginTop: 4 }}>
          {filename.replace(/^expense_\d+_/, '')}
        </span>
      </div>
    );
  }

  return src ? (
    <img 
      src={src} 
      alt="Receipt Preview" 
      style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 6 }} 
    />
  ) : (
    <div style={{ fontSize: "0.85rem", color: "var(--danger)", padding: 20 }}>Failed to load preview</div>
  );
};

export default function FinancePage() {
  const { user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Stats State
  const [stats, setStats] = useState({
    total_expenses: 0,
    total_expected_fees: 0,
    total_collected_fees: 0,
    total_discounts: 0,
    net_balance: 0
  });

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("current");
  const [activeCampYear, setActiveCampYear] = useState("2027");

  // Families State
  const [families, setFamilies] = useState([]);
  const [familySearch, setFamilySearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState("all"); // all, unpaid, partial, paid
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [reminderMode, setReminderMode] = useState("balance"); // balance, activity
  const [reminderTemplates, setReminderTemplates] = useState({
    balance: `Hi {first_name} {last_name},

This is a friendly reminder from GCA Church Camp regarding your camp fee balance. Your total outstanding balance is \${balance}. We'd greatly appreciate it if you could arrange payment at your earliest convenience.

If you've already taken care of this, please disregard this message — and thank you for your prompt attention!

We're so grateful to have you with us this year. If you have any questions, please don't hesitate to reach out.

Warm regards,
GCA Church Camp Team`
  });
  const [reminderFilter, setReminderFilter] = useState("due"); // due, all
  const [markingReminder, setMarkingReminder] = useState(null); // family_group currently being marked
  const [templateSaving, setTemplateSaving] = useState(false);

  // Dynamic Activity Configs
  const [activityNames, setActivityNames] = useState(["Kayaking", "Boat Tour"]);
  const [activityPrices, setActivityPrices] = useState([10.0, 20.0]);
  const [actPricesForm, setActPricesForm] = useState(["10.0", "20.0"]);

  // Apparel (T-shirt) Fee Config
  const [apparelPrice, setApparelPrice] = useState(10.0);
  const [apparelPriceForm, setApparelPriceForm] = useState("10.0");

  // Expenses State
  const [expenses, setExpenses] = useState([]);
  const [expenseSearch, setExpenseSearch] = useState("");
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState("All");

  // Fee Rates Pricing Config State
  const [rates, setRates] = useState([]);
  const [isRatesModalOpen, setIsRatesModalOpen] = useState(false);
  const [ratesForm, setRatesForm] = useState([]);

  // Modals State
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeExpense, setActiveExpense] = useState(null); // null means new, otherwise expense object
  const [selectedFile, setSelectedFile] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    description: "",
    category: "Catering",
    amount: "",
    date: new Date().toISOString().split("T")[0]
  });

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [activeFamily, setActiveFamily] = useState(null); // family object
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: "",
    status: "unpaid",
    notes: ""
  });

  // Merge Families State
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergeTargetFg, setMergeTargetFg] = useState("");
  const [mergeSourceFg, setMergeSourceFg] = useState("");
  const [merging, setMerging] = useState(false);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 4000);
  };

  const flashError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 5000);
  };

  const canReadFinance = hasPermission("finance", "read");
  const canUploadReceipts = hasPermission("receipt_upload", "edit") || hasPermission("receipt_upload", "read");

  useEffect(() => {
    if (!canReadFinance && canUploadReceipts) {
      setActiveTab("expenses");
    }
  }, [canReadFinance, canUploadReceipts]);

  // Fetch Data
  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (!canReadFinance && canUploadReceipts) {
        const expensesRes = await api.get(`/api/finance/expenses?${params}`);
        setExpenses(expensesRes.data.expenses);
      } else {
        const [statsRes, feesRes, expensesRes, ratesRes, settingsRes] = await Promise.all([
          api.get(`/api/finance/stats?${params}`),
          api.get(`/api/finance/fees?${params}`),
          api.get(`/api/finance/expenses?${params}`),
          api.get("/api/finance/rates"),
          api.get("/api/settings/"),
        ]);
        setStats(statsRes.data);
        setFamilies(feesRes.data.families);
        setExpenses(expensesRes.data.expenses);
        setRates(ratesRes.data.rates);
        if (statsRes.data.years) {
          setYears(statsRes.data.years);
        }
        if (statsRes.data.current_year) {
          setActiveCampYear(statsRes.data.current_year);
        }
        if (feesRes.data.activity_names) {
          setActivityNames(feesRes.data.activity_names);
        }
        if (feesRes.data.activity_prices) {
          setActivityPrices(feesRes.data.activity_prices);
        }
        if (feesRes.data.apparel_price !== undefined) {
          setApparelPrice(feesRes.data.apparel_price);
        }
        if (settingsRes.data.settings?.reminder_balance_template) {
          setReminderTemplates(prev => ({ ...prev, balance: settingsRes.data.settings.reminder_balance_template }));
        }
      }
    } catch (err) {
      flashError(err.response?.data?.error || "Failed to load financial records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Expand / Collapse Family Detail
  const toggleFamilyExpand = (familyGroup) => {
    setExpandedFamilies(prev => ({
      ...prev,
      [familyGroup]: !prev[familyGroup]
    }));
  };

  // ---- Fee Reminder helpers (free SMS / WhatsApp deep links, no third-party service) ----

  const buildReminderMessage = (family) => {
    const balance = ((family.total_expected_fee || 0) - (family.amount_paid || 0)).toFixed(2);
    const activityFee = (family.activity_fee || 0).toFixed(2);
    const apparelFee = (family.apparel_fee || 0).toFixed(2);
    const template = reminderTemplates[reminderMode];

    return template
      .replaceAll("{first_name}", family.head_first_name || "there")
      .replaceAll("{last_name}", family.head_last_name || "")
      .replaceAll("{name}", family.head_full_name || family.display_name || "there")
      .replaceAll("{family}", family.family_group)
      .replaceAll("{balance}", balance)
      .replaceAll("{activity_fee}", activityFee)
      .replaceAll("{apparel_fee}", apparelFee);
  };

  // Normalize a stored phone number into digits-only, assuming US numbers by
  // default (10 digits -> prefix with 1) if no country code is already present.
  const normalizePhoneDigits = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    if (!digits) return null;
    if (digits.length === 10) return `1${digits}`;
    return digits;
  };

  const buildSmsHref = (phone, message) => {
    const digits = normalizePhoneDigits(phone);
    if (!digits) return null;
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const separator = isIOS ? "&" : "?";
    return `sms:+${digits}${separator}body=${encodeURIComponent(message)}`;
  };

  const buildWhatsAppHref = (phone, message) => {
    const digits = normalizePhoneDigits(phone);
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  };

  const handleMarkReminderSent = async (familyGroup) => {
    setMarkingReminder(familyGroup);
    try {
      const res = await api.post("/api/finance/fees/mark-reminder-sent", { family_group: familyGroup });
      setFamilies(prev => prev.map(f =>
        f.family_group === familyGroup ? { ...f, reminder_sent_at: res.data.payment.reminder_sent_at } : f
      ));
      flashSuccess("Marked reminder as sent.");
    } catch (err) {
      flashError(err.response?.data?.error || "Failed to mark reminder as sent.");
    } finally {
      setMarkingReminder(null);
    }
  };

  // Expense CRUD
  const handleOpenExpenseModal = (expense = null) => {
    setSelectedFile(null);
    if (expense) {
      setActiveExpense(expense);
      setExpenseForm({
        description: expense.description,
        category: expense.category,
        amount: expense.amount,
        date: expense.date,
        refund_for_expense_id: expense.refund_for_expense_id || ""
      });
    } else {
      setActiveExpense(null);
      setExpenseForm({
        description: "",
        category: "Catering",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        refund_for_expense_id: ""
      });
    }
    setIsExpenseModalOpen(true);
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    setError("");
    if (!expenseForm.description || !expenseForm.amount || !expenseForm.date) {
      setError("Please fill out all required fields.");
      return;
    }

    try {
      let savedExpenseId = null;
      const amtFloat = parseFloat(expenseForm.amount);
      const payload = {
        description: expenseForm.description,
        category: expenseForm.category,
        amount: amtFloat,
        date: expenseForm.date,
        refund_for_expense_id: amtFloat < 0 && expenseForm.refund_for_expense_id ? parseInt(expenseForm.refund_for_expense_id) : null
      };

      if (activeExpense) {
        // Update
        await api.put(`/api/finance/expenses/${activeExpense.id}`, payload);
        savedExpenseId = activeExpense.id;
        flashSuccess("Expense updated successfully!");
      } else {
        // Create
        const res = await api.post("/api/finance/expenses", payload);
        savedExpenseId = res.data.expense.id;
        flashSuccess("Expense added successfully!");
      }

      if (selectedFile && savedExpenseId) {
        const formData = new FormData();
        formData.append("receipt", selectedFile);
        await api.post(`/api/finance/expenses/${savedExpenseId}/receipt`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      }

      setIsExpenseModalOpen(false);
      fetchFinanceData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save expense.");
    }
  };

  const handleDownloadReceipt = async (expenseId, filename) => {
    try {
      setSuccess("");
      setError("");
      
      const response = await api.get(`/api/finance/expenses/${expenseId}/receipt`, {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      flashSuccess("Receipt downloaded successfully!");
    } catch (err) {
      console.error(err);
      setError("Failed to download receipt.");
    }
  };

  const handleDownloadReceiptsZip = async () => {
    try {
      setSuccess("");
      setError("");
      
      const response = await api.get(`/api/finance/expenses/receipts-zip?year=${selectedYear}`, {
        responseType: "blob",
      });
      
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "gca_expenses_receipts.zip");
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      flashSuccess("All expense receipts ZIP downloaded successfully!");
    } catch (err) {
      console.error(err);
      setError("Failed to download receipts ZIP archive. Are there any uploaded files?");
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!await window.confirm("Are you sure you want to permanently delete this receipt?")) return;
    try {
      setSuccess("");
      setError("");
      await api.delete(`/api/finance/expenses/${expenseId}/receipt`);
      flashSuccess("Receipt deleted successfully!");
      fetchFinanceData();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to delete receipt.");
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!await window.confirm("Are you sure you want to permanently delete this expense?")) return;
    try {
      await api.delete(`/api/finance/expenses/${expenseId}`);
      flashSuccess("Expense deleted successfully!");
      fetchFinanceData();
    } catch (err) {
      flashError(err.response?.data?.error || "Failed to delete expense.");
    }
  };

  const handleSetHeadOfFamily = async (family_group, head_camper_id) => {
    try {
      await api.post("/api/finance/set-head", { family_group, head_camper_id });
      flashSuccess("Head of Family updated successfully!");
      fetchFinanceData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update Head of Family.");
    }
  };

  // Payment CRUD
  const handleOpenPaymentModal = (family) => {
    setActiveFamily(family);
    setPaymentForm({
      amount_paid: family.amount_paid,
      status: family.status,
      notes: family.notes || "",
      override_fee: family.override_fee !== null ? family.override_fee : "",
      discount: family.discount !== null && family.discount !== undefined ? family.discount : "",
      head_camper_id: family.head_camper_id || (family.members?.[0]?.id || "")
    });
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/api/finance/fees", {
        family_group: activeFamily.family_group,
        amount_paid: paymentForm.amount_paid,
        status: paymentForm.status,
        notes: paymentForm.notes,
        override_fee: paymentForm.override_fee !== "" ? paymentForm.override_fee : null,
        discount: paymentForm.discount !== "" ? parseFloat(paymentForm.discount) : 0.0
      });

      if (user?.role === "owner" && paymentForm.head_camper_id && parseInt(paymentForm.head_camper_id) !== activeFamily.head_camper_id) {
        await api.post("/api/finance/set-head", {
          family_group: activeFamily.family_group,
          head_camper_id: parseInt(paymentForm.head_camper_id)
        });
      }

      flashSuccess(`Payment details for '${activeFamily.display_name}' saved!`);
      setIsPaymentModalOpen(false);
      fetchFinanceData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to record payment details.");
    }
  };

  // Merge Families Handlers
  const handleOpenMergeModal = (sourceFamily = null) => {
    if (sourceFamily && sourceFamily.family_group) {
      setMergeSourceFg(sourceFamily.family_group);
      setMergeTargetFg("");
    } else {
      setMergeSourceFg("");
      setMergeTargetFg("");
    }
    setIsMergeModalOpen(true);
  };

  const handleExecuteMerge = async () => {
    if (!mergeTargetFg || !mergeSourceFg || mergeTargetFg === mergeSourceFg) return;
    setMerging(true);
    setError("");
    try {
      const res = await api.post("/api/finance/merge-families", {
        target_family_group: mergeTargetFg,
        source_family_group: mergeSourceFg
      });
      flashSuccess(res.data.message || "Families grouped successfully!");
      setIsMergeModalOpen(false);
      fetchFinanceData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to group families.");
    } finally {
      setMerging(false);
    }
  };

  // Rates Pricing Configuration handlers
  const handleOpenRatesModal = () => {
    setRatesForm(rates.map(r => ({ ...r })));
    setActPricesForm(
      activityNames.map((name, idx) => {
        return activityPrices[idx] !== undefined ? String(activityPrices[idx]) : "15.0";
      })
    );
    setApparelPriceForm(apparelPrice !== undefined ? String(apparelPrice) : "10.0");
    setIsRatesModalOpen(true);
  };

  const handleRateFormChange = (index, value) => {
    setRatesForm(prev => {
      const copy = [...prev];
      copy[index].price = value;
      return copy;
    });
  };

  const handleSaveRates = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/api/finance/rates", { rates: ratesForm });
      if (user?.role === "owner") {
        const payload = {};
        activityNames.forEach((name, idx) => {
          payload[`activity_${idx + 1}_price`] = actPricesForm[idx] || "15.0";
        });
        payload["apparel_price"] = apparelPriceForm || "10.0";
        await api.post("/api/settings/", payload);
      }
      flashSuccess("Pricing rates saved successfully!");
      setIsRatesModalOpen(false);
      fetchFinanceData();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save fee pricing rates.");
    }
  };

  const handlePrintPDF = async () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      await alert("Please allow popups to print/export PDF.");
      return;
    }
    
    const statsHTML = `
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-label">Expected Fees</div>
          <div class="metric-value">$${(stats.total_expected_fees || 0).toFixed(2)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Collected Fees</div>
          <div class="metric-value">$${(stats.total_collected_fees || 0).toFixed(2)}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Total Expenses</div>
          <div class="metric-value" style="color: ${stats.total_expenses < 0 ? '#27ae60' : '#2c3e50'}">
            ${stats.total_expenses < 0 ? `-$${Math.abs(stats.total_expenses).toFixed(2)}` : `$${(stats.total_expenses || 0).toFixed(2)}`}
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Net Balance</div>
          <div class="metric-value" style="color: ${stats.net_balance >= 0 ? '#27ae60' : '#c0392b'}">$${(stats.net_balance || 0).toFixed(2)}</div>
        </div>
      </div>
    `;

    const escapeHtml = (str) => {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    const totalReg = families.reduce((sum, f) => sum + (f.calculated_fee || 0), 0);
    const totalAct = families.reduce((sum, f) => sum + (f.activity_fee || 0), 0);
    const totalApparel = families.reduce((sum, f) => sum + (f.apparel_fee || 0), 0);
    const totalExp = families.reduce((sum, f) => sum + (f.total_expected_fee || 0), 0);
    const totalPaid = families.reduce((sum, f) => sum + (f.amount_paid || 0), 0);

    const feesRows = families.map(f => `
      <tr>
        <td>Family #${escapeHtml(f.family_group)} (${escapeHtml(f.display_name)})</td>
        <td>${f.eligible_count}</td>
        <td>$${(f.calculated_fee || 0).toFixed(2)}</td>
        <td>$${(f.activity_fee || 0).toFixed(2)}</td>
        <td>$${(f.apparel_fee || 0).toFixed(2)}</td>
        <td>$${(f.total_expected_fee || 0).toFixed(2)}</td>
        <td>$${(f.amount_paid || 0).toFixed(2)}</td>
        <td>${escapeHtml(f.status.toUpperCase())}</td>
      </tr>
    `).join("") + `
      <tr style="font-weight: bold; background-color: #eee;">
        <td>Total</td>
        <td></td>
        <td>$${totalReg.toFixed(2)}</td>
        <td>$${totalAct.toFixed(2)}</td>
        <td>$${totalApparel.toFixed(2)}</td>
        <td>$${totalExp.toFixed(2)}</td>
        <td>$${totalPaid.toFixed(2)}</td>
        <td></td>
      </tr>
    `;

    const expensesRows = expenses.map(e => `
      <tr>
        <td>${escapeHtml(e.date)}</td>
        <td>${escapeHtml(e.category)}</td>
        <td>${escapeHtml(e.description)}</td>
        <td>${e.amount < 0 ? `-$${Math.abs(e.amount).toFixed(2)}` : `$${(e.amount || 0).toFixed(2)}`}</td>
      </tr>
    `).join("");

    const currentYear = new Date().getFullYear();

    const htmlContent = `
      <html>
        <head>
          <title>GCA ${currentYear} Church Camp - Financial Report</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #2c3e50; padding: 20px; }
            h1 { color: #1e4d2b; border-bottom: 2px solid #1e4d2b; padding-bottom: 10px; margin-bottom: 20px; }
            h2 { color: #1e4d2b; margin-top: 30px; font-size: 1.25rem; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
            .metrics-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .metric-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 15px; text-align: center; }
            .metric-label { font-size: 0.75rem; text-transform: uppercase; color: #7f8c8d; font-weight: 600; margin-bottom: 5px; }
            .metric-value { font-size: 1.5rem; font-weight: 700; color: #2c3e50; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 0.875rem; }
            th, td { border: 1px solid #e0e0e0; padding: 10px 12px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: 600; }
            tr:nth-child(even) { background-color: #fafafa; }
            .print-btn-bar { margin-bottom: 20px; display: flex; gap: 10px; }
            @media print {
              .print-btn-bar { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="print-btn-bar">
            <button onclick="window.print()" style="padding: 10px 20px; background: #1e4d2b; color: white; border: none; border-radius: 4px; font-weight: 600; cursor: pointer;">🖨️ Print / Save as PDF</button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #e0e0e0; color: #333; border: none; border-radius: 4px; font-weight: 600; cursor: pointer;">Close</button>
          </div>
          <h1>Grace Christian Assembly - GCA ${currentYear} Church Camp Financial Report</h1>
          
          ${statsHTML}

          <h2>Camp Registration Fees</h2>
          <table>
            <thead>
              <tr>
                <th>Family Group</th>
                <th>Eligible Members</th>
                <th>Registration Fee</th>
                <th>Activity Fee</th>
                <th>Apparel Fee</th>
                <th>Total expected</th>
                <th>Amount Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${feesRows}
            </tbody>
          </table>

          <h2>Itemized Expenses</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${expensesRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportCSV = () => {
    let csvContent = "\ufeff"; // Add UTF-8 BOM for Excel compatibility
    
    // 1. Title & Summary Metrics
    csvContent += "Grace Christian Assembly Camp - Financial Report\n\n";
    csvContent += "SUMMARY METRICS\n";
    csvContent += "Expected Fees,Collected Fees,Total Discounts,Total Expenses,Net Balance\n";
    csvContent += `"${(stats.total_expected_fees || 0).toFixed(2)}","${(stats.total_collected_fees || 0).toFixed(2)}","${(stats.total_discounts || 0).toFixed(2)}","${(stats.total_expenses || 0).toFixed(2)}","${(stats.net_balance || 0).toFixed(2)}"\n\n`;

    // 2. Family Camp Fees Table
    csvContent += "CAMP REGISTRATION FEES\n";
    csvContent += "Family Group,Display Name,Eligible Member Count,Registration Fee,Church Discount,Activity Fee,Apparel Fee,Total Expected Fee,Paid Amount,Payment Status,Notes\n";
    families.forEach(f => {
      csvContent += `"${f.family_group}","${f.display_name}","${f.eligible_count}","${(f.calculated_fee || 0).toFixed(2)}","${(f.discount || 0).toFixed(2)}","${(f.activity_fee || 0).toFixed(2)}","${(f.apparel_fee || 0).toFixed(2)}","${(f.total_expected_fee || 0).toFixed(2)}","${(f.amount_paid || 0).toFixed(2)}","${f.status}","${(f.notes || "").replace(/"/g, '""')}"\n`;
    });
    const totalReg = families.reduce((sum, f) => sum + (f.calculated_fee || 0), 0);
    const totalDisc = families.reduce((sum, f) => sum + (f.discount || 0), 0);
    const totalAct = families.reduce((sum, f) => sum + (f.activity_fee || 0), 0);
    const totalApparel = families.reduce((sum, f) => sum + (f.apparel_fee || 0), 0);
    const totalExp = families.reduce((sum, f) => sum + (f.total_expected_fee || 0), 0);
    const totalPaid = families.reduce((sum, f) => sum + (f.amount_paid || 0), 0);
    csvContent += `"Total","","","${totalReg.toFixed(2)}","${totalDisc.toFixed(2)}","${totalAct.toFixed(2)}","${totalApparel.toFixed(2)}","${totalExp.toFixed(2)}","${totalPaid.toFixed(2)}","",""\n`;
    csvContent += "\n";

    // 3. Expenses Table
    csvContent += "ITEMIZED EXPENSES\n";
    csvContent += "Date,Category,Description,Amount\n";
    expenses.forEach(e => {
      csvContent += `"${e.date}","${e.category}","${(e.description || "").replace(/"/g, '""')}","${(e.amount || 0).toFixed(2)}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `gca_camp_financial_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filters & Alphabetical Sorting by Head of Family's Last Name
  const filteredFamilies = families
    .filter(f => {
      const matchesSearch = f.display_name.toLowerCase().includes(familySearch.toLowerCase()) || 
        f.members.some(m => m.full_name.toLowerCase().includes(familySearch.toLowerCase()));
      const matchesStatus = familyFilter === "all" || f.status === familyFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const lastA = (a.head_last_name || "").trim();
      const lastB = (b.head_last_name || "").trim();
      const firstA = (a.head_first_name || "").trim();
      const firstB = (b.head_first_name || "").trim();

      const lastComp = lastA.localeCompare(lastB, undefined, { sensitivity: "base", numeric: true });
      if (lastComp !== 0) return lastComp;
      return firstA.localeCompare(firstB, undefined, { sensitivity: "base", numeric: true });
    });

  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.description.toLowerCase().includes(expenseSearch.toLowerCase()) ||
      e.category.toLowerCase().includes(expenseSearch.toLowerCase());
    const matchesCategory = expenseCategoryFilter === "All" || e.category === expenseCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Overview chart data
  const ovPaid    = families.filter(f => f.status === "paid");
  const ovPartial = families.filter(f => f.status === "partial");
  const ovUnpaid  = families.filter(f => f.status === "unpaid");
  const ovExpected   = stats.total_expected_fees || 0;
  const ovCollected  = stats.total_collected_fees || 0;
  const ovCollectPct = ovExpected > 0 ? Math.min(100, (ovCollected / ovExpected) * 100) : 0;
  const ovTotalReg = families.reduce((s, f) => s + (f.calculated_fee || 0), 0);
  const ovTotalAct = families.reduce((s, f) => s + (f.activity_fee   || 0), 0);
  const ovTotalApp = families.reduce((s, f) => s + (f.apparel_fee    || 0), 0);
  const ovStatusData = [
    { label: `Paid (${ovPaid.length})`,    value: ovPaid.length,    color: "#27AE60" },
    { label: `Partial (${ovPartial.length})`, value: ovPartial.length, color: "#E67E22" },
    { label: `Unpaid (${ovUnpaid.length})`, value: ovUnpaid.length,  color: "#C0392B" },
  ].filter(d => d.value > 0);
  const ovAmtData = [
    { label: "Collected",    value: ovCollected,                        color: "#27AE60" },
    { label: "Outstanding",  value: Math.max(0, ovExpected - ovCollected), color: "#C0392B" },
  ].filter(d => d.value > 0);
  const ovFeeTypeData = [
    { label: "Registration", value: ovTotalReg, color: "#1E4D2B" },
    { label: "Activities",   value: ovTotalAct, color: "#3D8B50" },
    { label: "Apparel",      value: ovTotalApp, color: "#C8972B" },
  ].filter(d => d.value > 0);
  const ovExpCatData = EXPENSE_CATEGORIES.map(cat => ({
    label: cat,
    value: expenses.filter(e => e.category === cat && e.amount > 0).reduce((s, e) => s + e.amount, 0),
    color: CATEGORY_COLORS[cat],
  })).filter(d => d.value > 0);

    const grossRegistrationBase = filteredFamilies.reduce((sum, f) => {
      const base = f.override_fee !== null ? f.override_fee : f.tiered_fee;
      return sum + (base || 0);
    }, 0);
    const totalDiscounts = filteredFamilies.reduce((sum, f) => sum + (f.discount || 0), 0);
    const totalRegistrationBase = filteredFamilies.reduce((sum, f) => sum + (f.calculated_fee || 0), 0);
    const totalActivityFees = filteredFamilies.reduce((sum, f) => sum + (f.activity_fee || 0), 0);
    const totalExpectedCombined = filteredFamilies.reduce((sum, f) => sum + (f.total_expected_fee || 0), 0);
    const totalPaidCombined = filteredFamilies.reduce((sum, f) => sum + (f.amount_paid || 0), 0);
    const totalApparelFees = filteredFamilies.reduce((sum, f) => sum + (f.apparel_fee || 0), 0);

    if (!canReadFinance && !canUploadReceipts) {
      return (
        <div className="container" style={{ padding: "40px 20px" }}>
          <div className="alert alert-error">
            🔒 <strong>Access Denied:</strong> You do not have permission to view Finance or upload receipts.
          </div>
        </div>
      );
    }

    return (
    <div className="container" style={{ padding: "20px 0" }}>
      <style>{`
        @media (max-width: 768px) {
          .mobile-fab-btn {
            display: flex !important;
          }
          .header-action-btn-desktop {
            display: none !important;
          }
        }
      `}</style>

      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        flexDirection: "row", 
        flexWrap: "wrap", 
        justifyContent: "space-between", 
        alignItems: "center", 
        gap: "16px",
        marginBottom: 28 
      }}>
        <div style={{ minWidth: "280px", flex: "1 1 auto" }}>
          <h1 style={{ margin: 0, fontSize: "1.75rem", fontWeight: 600 }}>
            {canReadFinance ? "💰 Finance Manager" : "🧾 Expense & Receipt Submitter"}
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "var(--muted)", fontSize: "0.875rem" }}>
            {canReadFinance 
              ? "Track itemized camp expenses and collection of tiered family registration fees." 
              : "Submit camp expenses and upload vendor receipt images or documents."}
          </p>
        </div>
        <div style={{ 
          display: "flex", 
          flexWrap: "wrap", 
          gap: "8px", 
          alignItems: "center"
        }}>
          {(user?.role === "owner" || user?.role === "finance" || user?.role === "admin") && (
            <>
              <select 
                className="form-select" 
                style={{ width: 180, height: 38, padding: "0 10px", fontSize: "0.85rem", cursor: "pointer" }} 
                value={selectedYear} 
                onChange={e => setSelectedYear(e.target.value)}
              >
                <option value="current">Active Year ({activeCampYear})</option>
                {years.map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
              <button className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", fontSize: "0.85rem" }} onClick={handlePrintPDF}>
                📄 Print Report (PDF)
              </button>
              <button className="btn btn-outline" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", fontSize: "0.85rem" }} onClick={handleExportCSV}>
                📊 Export CSV (Excel)
              </button>
            </>
          )}
          {activeTab === "expenses" && (user?.role === "owner" || user?.role === "finance" || user?.role === "admin") && (
            <>
              <button 
                className="btn btn-outline" 
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", fontSize: "0.85rem" }} 
                onClick={() => setIsGalleryOpen(true)}
              >
                🖼️ View Receipts
              </button>
              <button 
                className="btn btn-outline" 
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", fontSize: "0.85rem" }} 
                onClick={handleDownloadReceiptsZip}
              >
                📦 Download Receipts (ZIP)
              </button>
            </>
          )}
          {activeTab === "fees" && hasPermission("finance", "edit") && (
            <>
              <button className="btn btn-outline" style={{ padding: "8px 12px", fontSize: "0.85rem" }} onClick={() => handleOpenMergeModal()}>
                🔗 Group Families
              </button>
              <button className="btn btn-outline" style={{ padding: "8px 12px", fontSize: "0.85rem" }} onClick={handleOpenRatesModal}>
                ⚙️ Configure Pricing
              </button>
            </>
          )}
          {activeTab === "expenses" && (hasPermission("finance", "edit") || hasPermission("receipt_upload", "edit")) && (
            <button className="btn btn-primary header-action-btn-desktop" style={{ padding: "8px 12px", fontSize: "0.85rem" }} onClick={() => handleOpenExpenseModal()}>
              ➕ Add Expense
            </button>
          )}
        </div>
      </div>

      {/* Error & Success banners */}
      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>⚠️ {error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>✅ {success}</div>}

      {/* Summary KPI Grid */}
      {canReadFinance && (
        <div className="stat-grid">
          <div className="stat-card" style={{ borderTop: "3px solid var(--forest)" }}>
            <div className="label">Expected Fees</div>
            <div className="value" style={{ fontSize: "1.7rem" }}>${(stats.total_expected_fees || 0).toFixed(2)}</div>
            <div style={{ fontSize: "0.75rem", color: "var(--muted)", display: "flex", flexDirection: "column", gap: 3, marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Base Registration</span>
                <strong style={{ color: "var(--charcoal)" }}>${grossRegistrationBase.toFixed(2)}</strong>
              </div>
              {totalDiscounts > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: "var(--gold)" }}>
                  <span>Church Discounts</span>
                  <strong>-${totalDiscounts.toFixed(2)}</strong>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Activity Fees</span>
                <strong style={{ color: "var(--forest-mid)" }}>${totalActivityFees.toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <div className="stat-card green-accent">
            <div className="label">Collected Fees</div>
            <div className="value" style={{ fontSize: "1.7rem", color: "var(--forest-lt)" }}>${(stats.total_collected_fees || 0).toFixed(2)}</div>
            <div className="sub">
              {stats.total_expected_fees > 0
                ? `${(((stats.total_collected_fees || 0) / stats.total_expected_fees) * 100).toFixed(1)}% of expectation`
                : "0% of expectation"}
            </div>
          </div>

          <div className="stat-card gold-accent">
            <div className="label">Church Discounts</div>
            <div className="value" style={{ fontSize: "1.7rem", color: "var(--gold)" }}>${(stats.total_discounts || 0).toFixed(2)}</div>
            <div className="sub">Total discounts granted</div>
          </div>

          <div className="stat-card" style={{ borderTop: `3px solid ${stats.total_expenses < 0 ? "var(--forest)" : "var(--danger)"}` }}>
            <div className="label">Total Expenses</div>
            <div className="value" style={{ fontSize: "1.7rem", color: stats.total_expenses < 0 ? "var(--forest)" : "var(--danger)" }}>
              {stats.total_expenses < 0 ? `-$${Math.abs(stats.total_expenses).toFixed(2)}` : `$${(stats.total_expenses || 0).toFixed(2)}`}
            </div>
            <div className="sub">Outflow items registered</div>
          </div>

          <div className="stat-card" style={{ borderTop: `3px solid ${stats.net_balance >= 0 ? "var(--forest-mid)" : "var(--danger)"}` }}>
            <div className="label">Net Balance</div>
            <div className="value" style={{ fontSize: "1.7rem", color: stats.net_balance >= 0 ? "var(--forest-mid)" : "var(--danger)" }}>
              ${(stats.net_balance || 0).toFixed(2)}
            </div>
            <div className="sub">Collected fees minus expenses</div>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      {canReadFinance && (
        <div style={{
          display: "flex",
          gap: 6,
          marginBottom: 24,
          flexWrap: "wrap"
        }}>
          {[
            { key: "overview",   icon: "📊", label: "Overview",          accent: "#2E6B3E", bg: "#E8F5EC" },
            { key: "fees",       icon: "🏷️", label: "Family Camp Fees",  accent: "#1E4D2B", bg: "#D6EAD9" },
            { key: "expenses",   icon: "🧾", label: "Itemized Expenses", accent: "#C0392B", bg: "#FDECEA" },
            { key: "reminders",  icon: "📱", label: "Send Reminders",    accent: "#92400E", bg: "#FEF3C7" }
          ].map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "9px 18px",
                  background: isActive ? tab.bg : "var(--parchment)",
                  border: `1.5px solid ${isActive ? tab.accent : "var(--border)"}`,
                  borderRadius: 8,
                  color: isActive ? tab.accent : "var(--muted)",
                  fontWeight: isActive ? 700 : 500,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  boxShadow: isActive ? `0 2px 8px rgba(0,0,0,0.08)` : "none",
                  transition: "background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s"
                }}
              >
                <span style={{ fontSize: "1rem" }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
          <span className="spinner" style={{ borderTopColor: "var(--forest-mid)", borderColor: "rgba(30,77,43,0.15)" }}></span>
          <div style={{ marginTop: 10, fontSize: "0.875rem" }}>Loading finance records…</div>
        </div>
      )}

      {!loading && activeTab === "overview" && canReadFinance && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Collection Progress */}
          <div className="card" style={{ padding: "22px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>💰 Collection Progress</h2>
              <span style={{ fontSize: "0.8rem", color: "var(--muted)" }}>
                ${ovCollected.toFixed(2)} collected of ${ovExpected.toFixed(2)} expected
              </span>
            </div>
            <div style={{ background: "var(--parchment)", borderRadius: 8, height: 26, overflow: "hidden" }}>
              <div style={{
                width: `${ovCollectPct}%`,
                height: "100%",
                background: ovCollectPct >= 100 ? "var(--forest-lt)" : ovCollectPct >= 60 ? "var(--forest-mid)" : "var(--gold)",
                borderRadius: 8,
                transition: "width 0.6s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: ovCollectPct > 12 ? 10 : 0,
              }}>
                {ovCollectPct > 12 && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#fff" }}>{ovCollectPct.toFixed(1)}%</span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 28, marginTop: 16, flexWrap: "wrap" }}>
              {[
                { label: "Fully Paid",    n: ovPaid.length,    sub: `$${ovPaid.reduce((s,f)=>s+(f.amount_paid||0),0).toFixed(2)}`,    color: "#27AE60" },
                { label: "Partial Paid",  n: ovPartial.length, sub: `$${ovPartial.reduce((s,f)=>s+(f.amount_paid||0),0).toFixed(2)} paid`, color: "#E67E22" },
                { label: "Unpaid",        n: ovUnpaid.length,  sub: `$${ovUnpaid.reduce((s,f)=>s+(f.total_expected_fee||0),0).toFixed(2)} owed`, color: "#C0392B" },
                { label: "Outstanding",   n: `$${Math.max(0, ovExpected - ovCollected).toFixed(2)}`, sub: `${(100 - ovCollectPct).toFixed(1)}% remaining`, color: "var(--muted)" },
              ].map((item, i) => (
                <div key={i}>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700, color: item.color }}>{item.n}</div>
                  <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--charcoal)" }}>{item.label}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment status charts */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 16 }}>
            <div className="card" style={{ padding: "22px 24px" }}>
              <h2 style={{ margin: "0 0 18px", fontSize: "1rem", fontWeight: 700 }}>👨‍👩‍👧 Families by Payment Status</h2>
              {families.length === 0
                ? <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No family data available.</p>
                : <SvgPieChart data={ovStatusData} size={160} />}
            </div>
            <div className="card" style={{ padding: "22px 24px" }}>
              <h2 style={{ margin: "0 0 18px", fontSize: "1rem", fontWeight: 700 }}>💵 Collected vs Outstanding</h2>
              {ovExpected === 0
                ? <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No fee data available.</p>
                : <SvgPieChart data={ovAmtData} size={160} />}
            </div>
          </div>

          {/* Fee breakdown + Expense by category */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: 16 }}>
            <div className="card" style={{ padding: "22px 24px" }}>
              <h2 style={{ margin: "0 0 18px", fontSize: "1rem", fontWeight: 700 }}>🏷️ Fee Type Breakdown</h2>
              {ovFeeTypeData.length === 0
                ? <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No fee data available.</p>
                : <>
                  <SvgPieChart data={ovFeeTypeData} size={160} />
                  <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                    {ovFeeTypeData.map((f, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
                        <span style={{ color: "var(--muted)" }}>{f.label}</span>
                        <strong>${f.value.toFixed(2)}</strong>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 2 }}>
                      <span style={{ fontWeight: 600 }}>Total Expected</span>
                      <strong>${(ovTotalReg + ovTotalAct + ovTotalApp).toFixed(2)}</strong>
                    </div>
                  </div>
                </>}
            </div>
            <div className="card" style={{ padding: "22px 24px" }}>
              <h2 style={{ margin: "0 0 18px", fontSize: "1rem", fontWeight: 700 }}>🧾 Expenses by Category</h2>
              {ovExpCatData.length === 0
                ? <p style={{ color: "var(--muted)", fontSize: "0.875rem" }}>No expense data yet.</p>
                : <>
                  <SvgPieChart data={ovExpCatData} size={160} />
                  <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                    {ovExpCatData.map((c, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
                        <span style={{ color: "var(--muted)" }}>{c.label}</span>
                        <strong>${c.value.toFixed(2)}</strong>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", borderTop: "1px solid var(--border)", paddingTop: 6, marginTop: 2 }}>
                      <span style={{ fontWeight: 600 }}>Total Expenses</span>
                      <strong>${ovExpCatData.reduce((s, c) => s + c.value, 0).toFixed(2)}</strong>
                    </div>
                  </div>
                </>}
            </div>
          </div>

          {/* Net balance summary */}
          <div className="card" style={{ padding: "22px 24px", borderTop: `3px solid ${(stats.net_balance || 0) >= 0 ? "var(--forest-mid)" : "var(--danger)"}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h2 style={{ margin: "0 0 4px", fontSize: "1rem", fontWeight: 700 }}>
                  {(stats.net_balance || 0) >= 0 ? "✅" : "⚠️"} Net Balance
                </h2>
                <p style={{ margin: 0, color: "var(--muted)", fontSize: "0.8rem" }}>
                  Total collected fees minus all recorded expenses
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: (stats.net_balance || 0) >= 0 ? "var(--forest-mid)" : "var(--danger)" }}>
                  ${(stats.net_balance || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                  ${ovCollected.toFixed(2)} collected − ${(stats.total_expenses || 0).toFixed(2)} expenses
                </div>
              </div>
            </div>
          </div>

        </div>
      )}

      {!loading && activeTab === "fees" && (
        <div>
          {/* Family Filters Panel */}
          <div className="search-bar">
            <div className="search-input-wrap">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="form-input"
                placeholder="Search family group or camper name…"
                value={familySearch}
                onChange={e => setFamilySearch(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={familyFilter}
              onChange={e => setFamilyFilter(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="all">All Payment Statuses</option>
              <option value="unpaid">❌ Unpaid</option>
              <option value="partial">⚠️ Partial Paid</option>
              <option value="paid">✅ Fully Paid</option>
            </select>
            <span className="text-muted" style={{ whiteSpace: "nowrap" }}>
              {filteredFamilies.length} of {families.length} {families.length === 1 ? "family" : "families"}
            </span>
          </div>

          {/* Families Table */}
          <div className="table-wrap">
            <table style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th>Family Group</th>
                  <th>Eligible Members (Age ≥ 5 or Null)</th>
                  <th>Registration Fee</th>
                  <th>Church Discount</th>
                  <th>Activity Fee</th>
                  <th>Apparel Fee</th>
                  <th>Total expected</th>
                  <th>Amount Paid</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFamilies.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: "center", padding: 20, color: "var(--muted)" }}>
                      No matching family fee records found.
                    </td>
                  </tr>
                ) : (
                  filteredFamilies.map(f => {
                    const isExpanded = !!expandedFamilies[f.family_group];
                    return (
                      <React.Fragment key={f.family_group}>
                        <tr style={{ borderBottom: isExpanded ? "none" : "1px solid var(--border)" }}>
                          <td>
                            <button 
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", padding: 4 }} 
                              onClick={() => toggleFamilyExpand(f.family_group)}
                            >
                              {isExpanded ? "▼" : "▶"}
                            </button>
                          </td>
                          <td>
                            <strong style={{ display: "block", fontSize: "0.95rem" }}>
                              {f.head_full_name ? f.head_full_name : f.display_name}
                            </strong>
                            <div style={{ fontSize: "0.8rem", color: "var(--forest-mid)", fontWeight: 500 }}>
                              {f.family_group && !f.family_group.startsWith("single-") ? `(Family #${f.family_group})` : "(Single Registration)"}
                            </div>
                            <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                              {f.members.length} {f.members.length === 1 ? "member" : "members"} total
                            </span>
                          </td>
                          <td>
                            <span style={{ fontWeight: 600 }}>{f.eligible_count}</span>
                            {f.members.length - f.eligible_count > 0 && (
                              <span style={{ fontSize: "0.75rem", color: "var(--muted)", marginLeft: 6 }}>
                                (Excluded: {f.members.length - f.eligible_count} under age 5)
                              </span>
                            )}
                          </td>
                          <td>
                            {f.is_overridden ? (
                              <div>
                                <strong style={{ color: "#e67e22" }}>${(f.calculated_fee || 0).toFixed(2)}</strong>
                                <span style={{ 
                                  display: "block", 
                                  fontSize: "0.675rem", 
                                  color: "var(--muted)", 
                                  textDecoration: "line-through" 
                                }}>
                                  Tier: ${(f.tiered_fee || 0).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <strong>${(f.calculated_fee || 0).toFixed(2)}</strong>
                            )}
                          </td>
                          <td>
                            {f.discount > 0 ? (
                              <strong style={{ color: "#d97706" }}>${(f.discount || 0).toFixed(2)}</strong>
                            ) : (
                              <span style={{ color: "var(--muted)" }}>—</span>
                            )}
                          </td>
                          <td>
                            {f.activity_fee > 0 ? (
                              <div>
                                <strong style={{ color: "var(--forest-mid)" }}>${(f.activity_fee || 0).toFixed(2)}</strong>
                                <span style={{ display: "block", fontSize: "0.65rem", color: "var(--muted)" }}>
                                  {[
                                    f.activity_1_spots > 0 && `${f.activity_1_spots}x ${activityNames[0] || "Kayaking"}`,
                                    f.activity_2_spots > 0 && `${f.activity_2_spots}x ${activityNames[1] || "Boat Tour"}`
                                  ].filter(Boolean).join(", ")}
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: "var(--muted)" }}>$0.00</span>
                            )}
                          </td>
                          <td>
                            {f.apparel_count > 0 ? (
                              <strong style={{ color: "var(--forest-mid)" }}>
                                ${(f.apparel_price ?? apparelPrice ?? 0).toFixed(2)} x {f.apparel_count} = ${(f.apparel_fee || 0).toFixed(2)}
                              </strong>
                            ) : (
                              <span style={{ color: "var(--muted)" }}>$0.00</span>
                            )}
                          </td>
                          <td>
                            <strong>${(f.total_expected_fee || 0).toFixed(2)}</strong>
                          </td>
                          <td style={{ color: f.amount_paid > 0 ? "#2ecc71" : "inherit" }}>
                            <strong>${(f.amount_paid || 0).toFixed(2)}</strong>
                          </td>
                          <td>
                            <span className={`badge ${
                              f.status === "paid" ? "badge-green" : f.status === "partial" ? "badge-gold" : "badge-red"
                            }`}>
                              {f.status === "paid" ? "Fully Paid" : f.status === "partial" ? "Partial" : "Unpaid"}
                            </span>
                          </td>
                          <td style={{ fontSize: "0.825rem", color: "var(--muted)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {f.notes || "—"}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {hasPermission("finance", "edit") && (
                              <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                                <button 
                                  className="btn btn-outline" 
                                  style={{ padding: "4px 8px", fontSize: "0.75rem" }} 
                                  onClick={() => handleOpenMergeModal(f)}
                                  title="Group this family into another family group"
                                >
                                  🔗 Group
                                </button>
                                <button 
                                  className="btn btn-outline" 
                                  style={{ padding: "4px 8px", fontSize: "0.75rem" }} 
                                  onClick={() => handleOpenPaymentModal(f)}
                                >
                                  ✏️ Record Payment
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: "rgba(0,0,0,0.02)" }}>
                            <td></td>
                            <td colSpan="9" style={{ padding: "8px 16px" }}>
                              <h5 style={{ margin: "0 0 8px 0", fontSize: "0.825rem", color: "var(--muted)" }}>Family Members Roster:</h5>
                              <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxWidth: "600px" }}>
                                {f.members.map(m => (
                                  <div key={m.id} style={{ 
                                    padding: "6px 12px", 
                                    background: "#fff", 
                                    border: "1px solid var(--border)", 
                                    borderRadius: 6,
                                    fontSize: "0.825rem",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}>
                                    <div>
                                      <span style={{ fontWeight: 600 }}>{m.full_name}</span>
                                      {user?.role === "owner" && (
                                        m.id === f.head_camper_id ? (
                                          <span className="badge badge-gold" style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 6px" }}>
                                            👑 Head of Family
                                          </span>
                                        ) : (
                                          <button 
                                            className="btn btn-ghost" 
                                            onClick={() => handleSetHeadOfFamily(f.family_group, m.id)}
                                            style={{ marginLeft: 8, fontSize: "0.7rem", padding: "2px 6px", border: "1px dashed var(--border)", cursor: "pointer" }}
                                            title="Designate as Head of Family (Owner Only)"
                                          >
                                            👑 Make Head
                                          </button>
                                        )
                                      )}
                                      {(m.kayaking > 0 || m.boat_tour > 0) && (
                                        <span style={{ marginLeft: 10, fontSize: "0.75rem", background: "var(--parchment)", padding: "2px 6px", borderRadius: 4, border: "1px solid var(--border)", color: "var(--forest)" }}>
                                          {[
                                            m.kayaking > 0 && `${m.kayaking}x ${activityNames[0] || "Kayaking"}`,
                                            m.boat_tour > 0 && `${m.boat_tour}x ${activityNames[1] || "Boat Tour"}`
                                          ].filter(Boolean).join(", ")}
                                        </span>
                                      )}
                                    </div>
                                    <span style={{ fontSize: "0.75rem", color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}>
                                      <span>Age: {m.age !== null ? m.age : "Unknown"}</span>
                                      <span title={m.is_eligible ? "Eligible (Age 5+ or unknown)" : "Under Age 5 (Excluded from fee count)"}>
                                        {m.is_eligible ? "✅" : "👶"}
                                      </span>
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr style={{ background: "var(--parchment)", fontWeight: "bold", borderTop: "2px solid var(--border)" }}>
                  <td></td>
                  <td>Total Summary</td>
                  <td></td>
                  <td>${totalRegistrationBase.toFixed(2)}</td>
                  <td>${totalDiscounts.toFixed(2)}</td>
                  <td>${totalActivityFees.toFixed(2)}</td>
                  <td>${totalApparelFees.toFixed(2)}</td>
                  <td>${totalExpectedCombined.toFixed(2)}</td>
                  <td>${totalPaidCombined.toFixed(2)}</td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {!loading && activeTab === "reminders" && (
        <div>
          <div className="alert" style={{ background: "#EAF4FB", color: "#1B4965", border: "1px solid #CFE6F5" }}>
            💬 <span>This opens your own phone's SMS or WhatsApp app with the message pre-filled — nothing is sent automatically and there's no per-message charge. Tap a button for each family, then hit Send in your messaging app to actually deliver it.</span>
          </div>

          <div className="card" style={{ padding: 16, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>
                Message template — Fee Balance Reminder
              </label>
              {hasPermission("finance", "edit") && (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={templateSaving}
                  onClick={async () => {
                    setTemplateSaving(true);
                    try {
                      await api.post("/api/settings/", { reminder_balance_template: reminderTemplates.balance });
                      flashSuccess("Message template saved.");
                    } catch {
                      flashError("Failed to save template.");
                    } finally {
                      setTemplateSaving(false);
                    }
                  }}
                >
                  {templateSaving ? "Saving…" : "💾 Save Template"}
                </button>
              )}
            </div>
            <textarea
              className="form-textarea"
              value={reminderTemplates[reminderMode]}
              onChange={(e) => setReminderTemplates(prev => ({ ...prev, [reminderMode]: e.target.value }))}
              rows={6}
            />
            <div className="text-muted" style={{ marginTop: 8 }}>
              Placeholders: <code>{"{first_name}"}</code>, <code>{"{last_name}"}</code>, <code>{"{name}"}</code>, <code>{"{family}"}</code>, <code>{"{balance}"}</code>, <code>{"{apparel_fee}"}</code>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
            <button
              className={`btn btn-sm ${reminderFilter === "due" ? "btn-primary" : "btn-outline"}`}
              onClick={() => setReminderFilter("due")}
            >
              Balance Due Only
            </button>
            <button
              className={`btn btn-sm ${reminderFilter === "all" ? "btn-primary" : "btn-outline"}`}
              onClick={() => setReminderFilter("all")}
            >
              All Families
            </button>
          </div>

          {(() => {
            const list = families.filter(f => {
              const balance = (f.total_expected_fee || 0) - (f.amount_paid || 0);
              return reminderFilter === "all" ? true : balance > 0.005;
            });

            if (list.length === 0) {
              return (
                <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
                  No families with an outstanding balance. 🎉
                </div>
              );
            }

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {list.map((family) => {
                  const balance = (family.total_expected_fee || 0) - (family.amount_paid || 0);
                  const message = buildReminderMessage(family);
                  const smsHref = buildSmsHref(family.contact_phone, message);
                  const waHref = buildWhatsAppHref(family.contact_phone, message);
                  const alreadySent = !!family.reminder_sent_at;

                  return (
                    <div key={family.family_group} className="card" style={{
                      padding: "14px 18px",
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 14,
                      justifyContent: "space-between"
                    }}>
                      <div style={{ minWidth: 220, flex: "1 1 260px" }}>
                        <div style={{ fontWeight: 700 }}>{family.display_name}</div>
                        <div style={{ fontSize: "0.8rem", color: "var(--muted)", marginTop: 2 }}>
                          {family.contact_phone ? family.contact_phone : "⚠️ No phone on file"}
                          {"  ·  "}Balance:{" "}
                          <span className={`badge ${balance > 0 ? "badge-red" : "badge-green"}`}>${balance.toFixed(2)}</span>
                        </div>
                        {alreadySent && (
                          <div style={{ fontSize: "0.75rem", color: "var(--forest-lt)", marginTop: 4 }}>
                            ✓ Reminder sent {new Date(family.reminder_sent_at).toLocaleString()}
                          </div>
                        )}
                      </div>

                      <div style={{ fontSize: "0.8rem", color: "var(--muted)", flex: "2 1 320px", fontStyle: "italic" }}>
                        "{message}"
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <a
                          href={smsHref || "#"}
                          onClick={(e) => { if (!smsHref) e.preventDefault(); }}
                          className="btn btn-sm"
                          style={{
                            background: smsHref ? "var(--forest)" : "var(--border)",
                            color: "#fff",
                            cursor: smsHref ? "pointer" : "not-allowed"
                          }}
                        >
                          💬 SMS
                        </a>
                        <a
                          href={waHref || "#"}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => { if (!waHref) e.preventDefault(); }}
                          className="btn btn-sm"
                          style={{
                            background: waHref ? "#25D366" : "var(--border)",
                            color: "#fff",
                            cursor: waHref ? "pointer" : "not-allowed"
                          }}
                        >
                          🟢 WhatsApp
                        </a>
                        <button
                          onClick={() => handleMarkReminderSent(family.family_group)}
                          disabled={markingReminder === family.family_group}
                          className="btn btn-sm btn-outline"
                        >
                          {markingReminder === family.family_group ? "..." : alreadySent ? "Mark Sent Again" : "Mark Sent"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {!loading && activeTab === "expenses" && (
        <div>
          {/* Expenses Filter Panel */}
          <div className="search-bar">
            <div className="search-input-wrap">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="form-input"
                placeholder="Search expenses by description…"
                value={expenseSearch}
                onChange={e => setExpenseSearch(e.target.value)}
              />
            </div>
            <select
              className="form-select"
              value={expenseCategoryFilter}
              onChange={e => setExpenseCategoryFilter(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="All">All Categories</option>
              {EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <span className="text-muted" style={{ whiteSpace: "nowrap" }}>
              {filteredExpenses.length} of {expenses.length} {expenses.length === 1 ? "expense" : "expenses"}
            </span>
          </div>

          {/* Expenses Table */}
          <div className="table-wrap">
            <table style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center", padding: 32, color: "var(--muted)" }}>
                      No expenses registered yet. Click "Add Expense" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map(e => (
                    <tr key={e.id}>
                      <td><strong>{e.description}</strong></td>
                      <td>
                        <span className="badge badge-gray">{e.category}</span>
                      </td>
                      <td style={{ 
                        color: e.amount < 0 ? "var(--forest)" : "var(--danger)", 
                        fontWeight: 600 
                      }}>
                        {e.amount < 0 ? `-$${Math.abs(e.amount).toFixed(2)}` : `$${(e.amount || 0).toFixed(2)}`}
                      </td>
                      <td>{e.date}</td>
                      <td style={{ textAlign: "right", display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                        {e.receipt_filename && (user?.role === "owner" || user?.role === "finance" || user?.role === "admin") && (
                          <button 
                            className="btn btn-outline" 
                            style={{ padding: "4px 8px", fontSize: "0.75rem" }} 
                            onClick={() => handleDownloadReceipt(e.id, e.receipt_filename)}
                            title="Download Attached Receipt"
                          >
                            📄 Receipt
                          </button>
                        )}
                        {hasPermission("finance", "edit") && (
                          <button 
                            className="btn btn-outline" 
                            style={{ padding: "4px 8px", fontSize: "0.75rem" }} 
                            onClick={() => handleOpenExpenseModal(e)}
                          >
                            ✏️ Edit
                          </button>
                        )}
                        {hasPermission("finance", "edit") && (
                          <button 
                            className="btn btn-outline" 
                            style={{ padding: "4px 8px", fontSize: "0.75rem", color: "var(--danger)" }} 
                            onClick={() => handleDeleteExpense(e.id)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expense Modal */}
      {isExpenseModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>{activeExpense ? "Edit Expense" : "Add New Expense"}</h2>
              <button className="modal-close" onClick={() => setIsExpenseModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSaveExpense}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Description *</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={expenseForm.description}
                  onChange={e => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g. Catering Deposit"
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Category *</label>
                <select 
                  className="form-input" 
                  value={expenseForm.category}
                  onChange={e => setExpenseForm(prev => ({ ...prev, category: e.target.value }))}
                  required
                >
                  {EXPENSE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Amount ($) *</label>
                <input 
                  type="number" 
                  step="0.01"
                  className="form-input" 
                  value={expenseForm.amount}
                  onChange={e => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>
              {parseFloat(expenseForm.amount) < 0 && (
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Refund for Expense *</label>
                  <select 
                    className="form-input" 
                    value={expenseForm.refund_for_expense_id}
                    onChange={e => setExpenseForm(prev => ({ ...prev, refund_for_expense_id: e.target.value }))}
                    required
                  >
                    <option value="">-- Select Original Expense --</option>
                    {expenses
                      .filter(exp => exp.amount > 0)
                      .map(exp => (
                        <option key={exp.id} value={exp.id}>
                          ID {exp.id}: {exp.description} (${exp.amount.toFixed(2)}) - {exp.date}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Date *</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={expenseForm.date}
                  onChange={e => setExpenseForm(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              {hasPermission("receipt_upload", "edit") && (
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Receipt File (Optional)</label>
                  <input 
                    type="file" 
                    className="form-input" 
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={e => setSelectedFile(e.target.files[0] || null)}
                    style={{ paddingTop: 8, paddingBottom: 8, height: "auto" }}
                  />
                  <span className="text-muted" style={{ fontSize: "0.7rem", marginTop: 4, display: "block" }}>
                    Supports PNG, JPG, JPEG, and PDF up to 5MB.
                  </span>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setIsExpenseModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {activeExpense ? "Save Changes" : "Create Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {isPaymentModalOpen && activeFamily && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Record Payment</h2>
              <button className="modal-close" onClick={() => setIsPaymentModalOpen(false)}>×</button>
            </div>
            <div style={{ fontSize: "0.875rem", margin: "0 0 16px 0", padding: "10px 12px", background: "var(--parchment)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ marginBottom: 4 }}>Family: <strong>{activeFamily.display_name}</strong></div>
              <div>Registration Fee: <strong>${(activeFamily.calculated_fee || 0).toFixed(2)}</strong> ({activeFamily.eligible_count} members)</div>
              {activeFamily.activity_fee > 0 && (
                <div>Activity Surcharge: <strong>${(activeFamily.activity_fee || 0).toFixed(2)}</strong></div>
              )}
              {activeFamily.apparel_fee > 0 && (
                <div>Apparel Fee: <strong>${(activeFamily.apparel_fee || 0).toFixed(2)}</strong></div>
              )}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--border)", fontWeight: 700 }}>
                Total Expected: <span>${(activeFamily.total_expected_fee || 0).toFixed(2)}</span>
              </div>
            </div>
            <form onSubmit={handleSavePayment}>
              {user?.role === "owner" && (
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Head of Family</label>
                  <select 
                    className="form-input" 
                    value={paymentForm.head_camper_id || ""}
                    onChange={e => setPaymentForm(prev => ({ ...prev, head_camper_id: e.target.value }))}
                  >
                    {activeFamily?.members?.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.full_name} {m.id === activeFamily.head_camper_id ? "👑 (Current Head)" : ""}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: "0.75rem", color: "var(--muted)", display: "block", marginTop: 4 }}>
                    Designate which member is the official Head of Family.
                  </span>
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Payment Amount Paid ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  value={paymentForm.amount_paid}
                  onChange={e => setPaymentForm(prev => ({ ...prev, amount_paid: e.target.value }))}
                  required
                />
                {(() => {
                  const expected = activeFamily.total_expected_fee || 0;
                  const paid = parseFloat(paymentForm.amount_paid) || 0;
                  const remaining = expected - paid;
                  return (
                    <span style={{
                      fontSize: "0.75rem",
                      display: "block",
                      marginTop: 4,
                      fontWeight: 600,
                      color: remaining > 0 ? "var(--danger)" : "var(--forest-lt)"
                    }}>
                      {remaining > 0
                        ? `Balance remaining: $${remaining.toFixed(2)}`
                        : remaining < 0
                        ? `Overpaid by $${Math.abs(remaining).toFixed(2)}`
                        : "Paid in full ✓"}
                    </span>
                  );
                })()}
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Payment Status *</label>
                <select 
                  className="form-input" 
                  value={paymentForm.status}
                  onChange={e => setPaymentForm(prev => ({ ...prev, status: e.target.value }))}
                  required
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial Paid</option>
                  <option value="paid">Fully Paid</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Church Discount ($)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-input" 
                  value={paymentForm.discount}
                  onChange={e => setPaymentForm(prev => ({ ...prev, discount: e.target.value }))}
                  placeholder="e.g. 50.00"
                />
                <span style={{ fontSize: "0.75rem", color: "var(--muted)", display: "block", marginTop: 4 }}>
                  Discount applied to reduce the family registration fee.
                </span>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">
                  Custom Fee Override ($) {!(user?.role === "owner" || user?.role === "finance") && "(Owner / Finance Only)"}
                </label>
                <input 
                  type="number" 
                  step="0.01"
                  min="0"
                  className="form-input" 
                  value={paymentForm.override_fee}
                  onChange={e => setPaymentForm(prev => ({ ...prev, override_fee: e.target.value }))}
                  placeholder="Using tiered rate calculation"
                  disabled={!(user?.role === "owner" || user?.role === "finance")}
                />
                {(user?.role === "owner" || user?.role === "finance") && (
                  <span style={{ fontSize: "0.75rem", color: "var(--muted)", display: "block", marginTop: 4 }}>
                    Ignores the default tiered rates for this family if set. Leave blank to reset.
                  </span>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Notes (Payment method, reference number, adjustments)</label>
                <textarea 
                  className="form-input" 
                  rows="3"
                  value={paymentForm.notes}
                  onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g. Check #402, paid in full on Sunday"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setIsPaymentModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Payment Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Configure Pricing Rates Modal */}
      {isRatesModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Configure Fee Rates</h2>
              <button className="modal-close" onClick={() => setIsRatesModalOpen(false)}>×</button>
            </div>
            <p style={{ fontSize: "0.875rem", margin: "0 0 16px 0", color: "var(--muted)" }}>
              Customize tiered camp registration fees. Changing these rates will automatically recalculate total expected fees.
            </p>
            <form onSubmit={handleSaveRates}>
              {ratesForm.map((rate, index) => {
                let tierLabel = "";
                if (rate.member_count === 1) tierLabel = "Single (1 Member)";
                else if (rate.member_count === 5) tierLabel = "5+ Members Family";
                else tierLabel = `${rate.member_count}-Person Family`;

                return (
                  <div className="form-group" style={{ marginBottom: 12 }} key={rate.member_count}>
                    <label className="form-label">{tierLabel} *</label>
                    <input 
                      type="number" 
                      step="0.01"
                      min="0"
                      className="form-input" 
                      value={rate.price}
                      onChange={e => handleRateFormChange(index, e.target.value)}
                      required
                    />
                  </div>
                );
              })}

              <h3 style={{ fontSize: "0.95rem", margin: "20px 0 12px 0", borderTop: "1px solid var(--border)", paddingTop: 16, color: "var(--forest)", fontWeight: 700 }}>
                🚴 Activity Prices per Spot
              </h3>
              {activityNames.map((name, idx) => (
                <div className="form-group" style={{ marginBottom: 12 }} key={idx}>
                  <label className="form-label">
                    {idx === 0 ? "🛶" : idx === 1 ? "🚤" : "🎯"} {name} Price ($) * {user?.role !== "owner" && "(Owner Only)"}
                  </label>
                  <input 
                    type="number" 
                    step="0.01"
                    min="0"
                    className="form-input" 
                    value={actPricesForm[idx] || ""}
                    onChange={e => {
                      const val = e.target.value;
                      setActPricesForm(prev => {
                        const copy = [...prev];
                        copy[idx] = val;
                        return copy;
                      });
                    }}
                    required
                    disabled={user?.role !== "owner"}
                  />
                </div>
              ))}

              <h3 style={{ fontSize: "0.95rem", margin: "20px 0 12px 0", borderTop: "1px solid var(--border)", paddingTop: 16, color: "var(--forest)", fontWeight: 700 }}>
                👕 Apparel Pricing
              </h3>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">
                  Apparel Fee (T-shirt Price) ($) * {user?.role !== "owner" && "(Owner Only)"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-input"
                  value={apparelPriceForm}
                  onChange={e => setApparelPriceForm(e.target.value)}
                  required
                  disabled={user?.role !== "owner"}
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setIsRatesModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Pricing Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Floating Action Button for Adding Expense */}
      {activeTab === "expenses" && (hasPermission("finance", "edit") || hasPermission("receipt_upload", "edit")) && (
        <button 
          className="mobile-fab-btn" 
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "var(--forest)",
            color: "#fff",
            border: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            display: "none",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "1.5rem",
            zIndex: 1000,
            cursor: "pointer"
          }}
          onClick={() => handleOpenExpenseModal()}
          title="Add Expense"
        >
          ➕
        </button>
      )}

      {/* Receipts Gallery Modal */}
      {isGalleryOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 800, width: "90%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div className="modal-header">
              <h2>Receipts</h2>
              <button className="modal-close" onClick={() => setIsGalleryOpen(false)}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 0" }}>
              {expenses.filter(e => e.receipt_filename).length === 0 ? (
                <div style={{ textAlign: "center", color: "var(--muted)", padding: 40 }}>
                  No receipts uploaded yet.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {expenses.filter(e => e.receipt_filename).map(e => (
                    <div 
                      key={e.id} 
                      className="card" 
                      style={{ 
                        padding: 16, 
                        display: "flex", 
                        flexDirection: "row", 
                        alignItems: "center", 
                        gap: 20,
                        flexWrap: "wrap"
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 12 }}>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <span style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--forest)" }}>{e.description}</span>
                            <span style={{ 
                               fontWeight: 700, 
                               fontSize: "1.05rem", 
                               color: (e.amount || 0) < 0 ? "var(--forest)" : "var(--danger)" 
                             }}>
                               {(e.amount || 0) < 0 ? `-$${Math.abs(e.amount).toFixed(2)}` : `$${(e.amount || 0).toFixed(2)}`}
                             </span>
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, fontSize: "0.8rem", color: "var(--muted)" }}>
                            <span>{e.date}</span>
                            <span>•</span>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: 12,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              background: "var(--border)",
                              color: "var(--charcoal)"
                            }}>
                              {e.category}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button 
                            className="btn btn-outline" 
                            style={{ padding: "6px 16px", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: 4 }} 
                            onClick={() => handleDownloadReceipt(e.id, e.receipt_filename)}
                          >
                            📥 Download
                          </button>
                          {(user?.role === "owner" || user?.role === "finance" || user?.role === "admin") && (
                            <button 
                              className="btn btn-outline" 
                              style={{ padding: "6px 16px", fontSize: "0.78rem", color: "var(--danger)", borderColor: "var(--danger)", display: "flex", alignItems: "center", gap: 4 }} 
                              onClick={() => handleDeleteReceipt(e.id)}
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ 
                        width: "220px", 
                        height: "140px", 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center", 
                        background: "rgba(0,0,0,0.01)", 
                        borderRadius: 6, 
                        overflow: "hidden",
                        border: "1px solid var(--border)"
                      }}>
                        <ReceiptPreview expenseId={e.id} filename={e.receipt_filename} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setIsGalleryOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Merge / Group Families Modal */}
      {isMergeModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2>🔗 Group Two Families Together</h2>
              <button className="modal-close" onClick={() => setIsMergeModalOpen(false)}>×</button>
            </div>

            <p style={{ fontSize: "0.85rem", color: "var(--muted)", marginBottom: 18 }}>
              Select two families to combine. All members from the secondary family will be reassigned to the primary family group. This reduces the total family count by 1 and automatically recalculates tiered fees for the combined family size.
            </p>

            <div className="form-group">
              <label className="form-label">Primary Family Group (Target) *</label>
              <select
                className="form-select"
                value={mergeTargetFg}
                onChange={e => setMergeTargetFg(e.target.value)}
              >
                <option value="">-- Select Primary Family --</option>
                {families.map(f => (
                  <option key={f.family_group} value={f.family_group} disabled={f.family_group === mergeSourceFg}>
                    {f.display_name} ({f.members.length} {f.members.length === 1 ? 'member' : 'members'})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Secondary Family to Merge (Source) *</label>
              <select
                className="form-select"
                value={mergeSourceFg}
                onChange={e => setMergeSourceFg(e.target.value)}
              >
                <option value="">-- Select Family to Merge --</option>
                {families.map(f => (
                  <option key={f.family_group} value={f.family_group} disabled={f.family_group === mergeTargetFg}>
                    {f.display_name} ({f.members.length} {f.members.length === 1 ? 'member' : 'members'})
                  </option>
                ))}
              </select>
            </div>

            {mergeTargetFg && mergeSourceFg && (
              <div className="alert" style={{ background: "rgba(30,77,43,0.06)", color: "var(--forest)", border: "1px solid rgba(30,77,43,0.15)" }}>
                💡 <span><strong>Summary:</strong> Members from <strong>{families.find(f => f.family_group === mergeSourceFg)?.display_name}</strong> will join <strong>{families.find(f => f.family_group === mergeTargetFg)?.display_name}</strong>.</span>
              </div>
            )}

            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setIsMergeModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!mergeTargetFg || !mergeSourceFg || mergeTargetFg === mergeSourceFg || merging}
                onClick={handleExecuteMerge}
              >
                {merging ? "Grouping..." : "🔗 Group Families Together"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
